/**
 * Base API client factory - request() with mock support in test env.
 */

import type { ApiResponse, ApiClientConfig } from "./types";
import {
  getMockKey,
  getMock,
  setMock,
  setMockFailure,
  clearMock,
  clearMocks,
} from "./mockRegistry";

const defaultIsTestEnv = (): boolean =>
  typeof process !== "undefined" && process.env?.NODE_ENV === "test";

export interface BaseApiClient {
  request<T>(
    endpoint: string,
    method: string,
    body?: unknown,
  ): Promise<ApiResponse<T>>;
  requestPublic<T>(
    endpoint: string,
    method: string,
    body?: unknown,
  ): Promise<ApiResponse<T>>;
  setMock(
    key: string,
    result: ApiResponse<unknown> | (() => ApiResponse<unknown>),
  ): void;
  setMockFailure(key: string, errorMessage: string): void;
  clearMock(key: string): void;
  clearMocks(): void;
}

export function createBaseApiClient(config: ApiClientConfig): BaseApiClient {
  const isTestEnv = config.isTestEnv ?? defaultIsTestEnv;
  const getBaseUrl = () =>
    typeof config.baseUrl === "function" ? config.baseUrl() : config.baseUrl;

  return {
    async request<T>(
      endpoint: string,
      method: string,
      body?: unknown,
    ): Promise<ApiResponse<T>> {
      if (isTestEnv()) {
        const key = getMockKey(endpoint, method, body);
        if (key) {
          const mock = getMock<T>(key);
          if (mock !== undefined) {
            const result = typeof mock === "function" ? mock() : mock;
            return result as ApiResponse<T>;
          }
        }
      }

      const token = config.getAuthToken ? await config.getAuthToken() : undefined;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const url = `${getBaseUrl()}${endpoint}`;
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      if (!response.ok) {
        const errData = data as Record<string, unknown>;
        const debugLogs = Array.isArray(errData.debugLogs)
          ? (errData.debugLogs as string[])
          : undefined;
        if (
          debugLogs?.length &&
          typeof process !== "undefined" &&
          process.env?.NODE_ENV !== "production"
        ) {
          console.error(
            `[API ${response.status}] ${(errData.error as string) || "Error"}\n  Debug logs:\n${debugLogs.map((l) => `    ${l}`).join("\n")}`,
          );
        }
        return {
          success: false,
          data: null,
          error: (errData.error as string) || `HTTP ${response.status}`,
          debugLogs,
        };
      }

      const okBody = data as Record<string, unknown>;
      if (
        okBody.success === false &&
        Array.isArray(okBody.debugLogs) &&
        okBody.debugLogs.length > 0 &&
        typeof process !== "undefined" &&
        process.env?.NODE_ENV !== "production"
      ) {
        console.error(
          `[API ${response.status}] ${String(okBody.error ?? "Error")}\n  Debug logs:\n${(okBody.debugLogs as string[]).map((l) => `    ${l}`).join("\n")}`,
        );
      }

      return data as ApiResponse<T>;
    },

    async requestPublic<T>(
      endpoint: string,
      method: string,
      body?: unknown,
    ): Promise<ApiResponse<T>> {
      if (isTestEnv()) {
        const key = getMockKey(endpoint, method, body);
        if (key) {
          const mock = getMock<T>(key);
          if (mock !== undefined) {
            const result = typeof mock === "function" ? mock() : mock;
            return result as ApiResponse<T>;
          }
        }
      }

      const url = `${getBaseUrl()}${endpoint}`;
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      if (!response.ok) {
        const errData = data as Record<string, unknown>;
        return {
          success: false,
          data: null,
          error: (errData.error as string) || `HTTP ${response.status}`,
        };
      }

      return data as ApiResponse<T>;
    },

    setMock(key, result) {
      setMock(key, result);
    },
    setMockFailure(key, errorMessage) {
      setMockFailure(key, errorMessage);
    },
    clearMock(key) {
      clearMock(key);
    },
    clearMocks,
  };
}

export type { ApiResponse, ApiClientConfig } from "./types";
